"use client"

import type React from "react"
import { PieChart, Clock, CheckCircle, Play } from "lucide-react"

interface ReviewStatusData {
  status: string
  count: number
}

interface ReviewStatusDistributionProps {
  data: ReviewStatusData[]
}

const ReviewStatusDistribution: React.FC<ReviewStatusDistributionProps> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.count, 0)
  const colors = ["#EF4444", "#F59E0B", "#10B981"] // Red, Amber, Green

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "not started":
        return <Clock className="h-4 w-4" />
      case "in progress":
        return <Play className="h-4 w-4" />
      case "completed":
        return <CheckCircle className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const getStatusColor = (index: number) => {
    return colors[index % colors.length]
  }

  if (total === 0) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-6">
          <PieChart className="h-6 w-6 text-purple-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-900">Review Status Distribution</h2>
        </div>
        <div className="text-center py-8">
          <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No review data available</p>
        </div>
      </div>
    )
  }

  // Calculate angles for the donut chart
  let currentAngle = 0
  const segments = data.map((item, index) => {
    const percentage = (item.count / total) * 100
    const angle = (item.count / total) * 360
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    currentAngle += angle

    // Calculate path for SVG arc
    const radius = 80
    const innerRadius = 50
    const centerX = 100
    const centerY = 100

    const startAngleRad = (startAngle * Math.PI) / 180
    const endAngleRad = (endAngle * Math.PI) / 180

    const x1 = centerX + radius * Math.cos(startAngleRad)
    const y1 = centerY + radius * Math.sin(startAngleRad)
    const x2 = centerX + radius * Math.cos(endAngleRad)
    const y2 = centerY + radius * Math.sin(endAngleRad)

    const x3 = centerX + innerRadius * Math.cos(endAngleRad)
    const y3 = centerY + innerRadius * Math.sin(endAngleRad)
    const x4 = centerX + innerRadius * Math.cos(startAngleRad)
    const y4 = centerY + innerRadius * Math.sin(startAngleRad)

    const largeArcFlag = angle > 180 ? 1 : 0

    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      "Z",
    ].join(" ")

    return {
      ...item,
      percentage,
      pathData,
      color: getStatusColor(index),
    }
  })

  return (
    <div className="w-full">
      <div className="flex items-center mb-6">
        <PieChart className="h-6 w-6 text-purple-600 mr-3" />
        <h2 className="text-xl font-bold text-gray-900">Review Status Distribution</h2>
      </div>

      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* Donut Chart */}
        <div className="relative">
          <svg width="200" height="200" className="transform -rotate-90">
            {segments.map((segment, index) => (
              <path
                key={segment.status}
                d={segment.pathData}
                fill={segment.color}
                className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                stroke="white"
                strokeWidth="2"
              />
            ))}
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-gray-900">{total}</span>
            <span className="text-sm text-gray-600">Total Reviews</span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-3">
          {segments.map((segment, index) => (
            <div key={segment.status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: segment.color }} />
                  {getStatusIcon(segment.status)}
                </div>
                <span className="font-medium text-gray-900">{segment.status}</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900">{segment.count}</div>
                <div className="text-sm text-gray-600">{segment.percentage.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        {segments.map((segment, index) => (
          <div key={segment.status} className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center mb-2" style={{ color: segment.color }}>
              {getStatusIcon(segment.status)}
            </div>
            <div className="font-bold text-gray-900">{segment.count}</div>
            <div className="text-xs text-gray-600">{segment.status}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ReviewStatusDistribution
